// Updated RecordsFormFields.jsx with merged metrics and button interfaces

import React, { useState, useEffect } from 'react';
import { 
  Database, Plus, Edit, Trash2, Search, Filter, Clock, Users, MapPin, ChevronDown,
  Building2, ClipboardList, FileText, AlertCircle, CheckCircle, AlertTriangle,
  Calendar, Timer, BarChart3, TrendingUp, TrendingDown, Activity,
  X, Eye, Download, Upload, RefreshCw, DollarSign, Target, Zap
} from 'lucide-react';
import { organisationService } from '../../../services/organisationService';

const ACTIVITY_TYPES = {
  meeting: { label: 'Meeting', icon: Users, color: 'blue' },
  task: { label: 'Task Execution', icon: ClipboardList, color: 'green' },
  maintenance: { label: 'Maintenance', icon: Building2, color: 'yellow' },
  training: { label: 'Training', icon: Users, color: 'purple' },
  client_interaction: { label: 'Client Interaction', icon: Users, color: 'indigo' },
  site_visit: { label: 'Site Visit', icon: MapPin, color: 'pink' },
  document_review: { label: 'Document Review', icon: FileText, color: 'gray' },
  project_milestone: { label: 'Project Milestone', icon: CheckCircle, color: 'green' },
  incident: { label: 'Incident/Issue', icon: AlertCircle, color: 'red' },
  general: { label: 'General Activity', icon: Activity, color: 'blue' }
};

const STATUS_TYPES = {
  green: { 
    label: 'On Track', 
    icon: CheckCircle, 
    color: 'text-green-700', 
    bg: 'bg-green-100',
    btnColor: 'bg-green-500 hover:bg-green-600 text-white',
    btnColorActive: 'bg-green-600 text-white shadow-lg',
    description: 'Activity completed successfully or progressing well'
  },
  orange: { 
    label: 'Needs Attention', 
    icon: AlertTriangle, 
    color: 'text-orange-700', 
    bg: 'bg-orange-100',
    btnColor: 'bg-orange-500 hover:bg-orange-600 text-white',
    btnColorActive: 'bg-orange-600 text-white shadow-lg',
    description: 'Minor issues or delays, requires monitoring'
  },
  red: { 
    label: 'Critical Issue', 
    icon: AlertCircle, 
    color: 'text-red-700', 
    bg: 'bg-red-100',
    btnColor: 'bg-red-500 hover:bg-red-600 text-white',
    btnColorActive: 'bg-red-600 text-white shadow-lg',
    description: 'Major problems requiring immediate attention'
  }
};

// Metric types that can be toggled on/off
const METRIC_TYPES = {
  time: { 
    label: 'Specific Time', 
    icon: Clock, 
    description: 'Record a specific point in time'
  },
  duration: { 
    label: 'Duration', 
    icon: Timer, 
    description: 'Track time period with start and end'
  },
  quantity: { 
    label: 'Quantity/Count', 
    icon: BarChart3, 
    description: 'Measure quantities, counts, or readings'
  },
  quality: { 
    label: 'Quality Score', 
    icon: Target, 
    description: 'Rate the quality of work (1-10)'
  },
  progress: { 
    label: 'Progress %', 
    icon: TrendingUp, 
    description: 'Track completion percentage'
  },
  amount: { 
    label: 'Financial Amount', 
    icon: DollarSign, 
    description: 'Track costs, income, or financial impact'
  }
};

export const RecordsFormFields = ({ bookId, templateType, onUpdate, accessLevel = 'internal' }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('week');
  const [viewMode, setViewMode] = useState('list');

  // Reference data from other management sections
  const [referenceData, setReferenceData] = useState({
    team: [],
    sites: [],
    assets: [],
    tasks: [],
    clients: [],
    documents: []
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    activityType: 'general',
    status: 'green',
    
    // Enabled metrics (user toggles these on/off)
    enabledMetrics: {
      duration: true,
      quality: false,
      progress: false,
      quantity: false,
      amount: false,
      time: false
    },
    
    // All metric values
    metrics: {
      // Time metric
      specificTime: '',
      
      // Duration metric
      durationType: 'datetime', // 'datetime', 'date', 'time'
      plannedDuration: '',
      startDateTime: '',
      endDateTime: '',
      startDateOnly: '',
      endDateOnly: '',
      startTimeOnly: '',
      endTimeOnly: '',
      actualDuration: '',
      
      // Quantity metric
      quantityType: 'single', // 'single', 'range'
      quantityValue: '',
      quantityStart: '',
      quantityEnd: '',
      quantityUnit: '',
      
      // Quality metric
      qualityScore: '',
      
      // Progress metric
      completionPercentage: 100,
      
      // Amount metric
      amountType: 'cost', // 'cost', 'income', 'saving'
      amountValue: '',
      amountCurrency: 'R'
    },
    
    // References to other entities
    references: {
      teamMembers: [],
      sites: [],
      assets: [],
      tasks: [],
      clients: [],
      documents: []
    },
    
    // Additional details
    challenges: '',
    achievements: '',
    nextActions: '',
    tags: [],
    attachments: [],
    isPublic: false
  });

  useEffect(() => {
    loadRecordsData();
    loadReferenceData();
  }, [bookId]);

  const loadRecordsData = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getManagementFeatureData(bookId, 'records');
      setRecords(data?.activities || []);
    } catch (error) {
      console.error('Error loading records data:', error);
    } finally {
      setLoading(false);
    }
  };

  console.log('Organisation records', records);

  const loadReferenceData = async () => {
    try {
      const [teamData, sitesData, assetsData, tasksData, clientsData, documentsData] = await Promise.all([
        organisationService.getManagementFeatureData(bookId, 'team'),
        organisationService.getManagementFeatureData(bookId, 'sites'),
        organisationService.getManagementFeatureData(bookId, 'assets'),
        organisationService.getManagementFeatureData(bookId, 'tasks'),
        organisationService.getManagementFeatureData(bookId, 'clients'),
        organisationService.getManagementFeatureData(bookId, 'documents')
      ]);

      setReferenceData({
        team: teamData?.members || [],
        sites: sitesData?.sites || [],
        assets: assetsData?.assets || [],
        tasks: tasksData?.tasks || [],
        clients: clientsData?.clients || [],
        documents: documentsData?.documents || []
      });
    } catch (error) {
      console.warn('Error loading reference data:', error);
    }
  };

  const saveRecordsData = async (updatedRecords) => {
    try {
      // Calculate analytics data
      const analytics = calculateAnalytics(updatedRecords);
      
      await organisationService.updateManagementFeatureData(bookId, 'records', {
        activities: updatedRecords,
        analytics: analytics,
        lastUpdated: new Date(),
        totalActivities: updatedRecords.length,
        statusDistribution: {
          green: updatedRecords.filter(r => r.status === 'green').length,
          orange: updatedRecords.filter(r => r.status === 'orange').length,
          red: updatedRecords.filter(r => r.status === 'red').length
        }
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error saving records data:', error);
      throw error;
    }
  };

  const calculateAnalytics = (activities) => {
    // Analytics calculation logic (same as before)
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const recentActivities = activities.filter(a => new Date(a.createdAt) >= weekAgo);
    const monthlyActivities = activities.filter(a => new Date(a.createdAt) >= monthAgo);

    return {
      totalHours: activities.reduce((sum, a) => sum + (a.metrics?.actualDuration || 0), 0) / 60,
      averageQuality: activities.filter(a => a.metrics?.qualityScore)
        .reduce((sum, a, _, arr) => sum + a.metrics.qualityScore / arr.length, 0),
      weeklyTrend: recentActivities.length,
      monthlyTrend: monthlyActivities.length,
      criticalIssues: activities.filter(a => a.status === 'red').length,
      completionRate: activities.filter(a => a.metrics?.completionPercentage === 100).length / activities.length * 100,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Auto-calculate duration if not provided but start/end times are set
      let metrics = { ...formData.metrics };
      if (formData.enabledMetrics.duration && !metrics.actualDuration) {
        if (metrics.durationType === 'datetime' && metrics.startDateTime && metrics.endDateTime) {
          const start = new Date(metrics.startDateTime);
          const end = new Date(metrics.endDateTime);
          metrics.actualDuration = Math.round((end - start) / (1000 * 60));
        }
      }

      const recordData = {
        ...formData,
        metrics,
        id: editingRecord?.id || Date.now().toString(),
        createdAt: editingRecord?.createdAt || new Date(),
        updatedAt: new Date(),
        createdBy: 'current-user',
        tags: Array.isArray(formData.tags) ? formData.tags : 
              typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()) : []
      };

      let updatedRecords;
      if (editingRecord) {
        updatedRecords = records.map(record => 
          record.id === editingRecord.id ? recordData : record
        );
      } else {
        updatedRecords = [recordData, ...records];
      }

      await saveRecordsData(updatedRecords);
      setRecords(updatedRecords);
      handleCloseForm();
    } catch (error) {
      alert('Error saving activity record: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this activity record?')) return;

    try {
      const updatedRecords = records.filter(record => record.id !== recordId);
      await saveRecordsData(updatedRecords);
      setRecords(updatedRecords);
    } catch (error) {
      alert('Error deleting record: ' + error.message);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      ...record,
      tags: Array.isArray(record.tags) ? record.tags.join(', ') : record.tags || ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setFormData({
      title: '', description: '', activityType: 'general', status: 'green',
      enabledMetrics: { duration: true, quality: false, progress: false, quantity: false, amount: false, time: false },
      metrics: {
        specificTime: '', durationType: 'datetime', plannedDuration: '', startDateTime: '', endDateTime: '',
        startDateOnly: '', endDateOnly: '', startTimeOnly: '', endTimeOnly: '', actualDuration: '',
        quantityType: 'single', quantityValue: '', quantityStart: '', quantityEnd: '', quantityUnit: '',
        qualityScore: '', completionPercentage: 100, amountType: 'cost', amountValue: '', amountCurrency: 'R'
      },
      references: { teamMembers: [], sites: [], assets: [], tasks: [], clients: [], documents: [] },
      challenges: '', achievements: '', nextActions: '', tags: [], attachments: [], isPublic: false
    });
  };

  // Filter records (same logic as before)
  const filteredRecords = records.filter(record => {
    const matchesSearch = record.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        record.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || record.status === filterStatus;
    const matchesType = filterType === 'all' || record.activityType === filterType;
    
    // Date range filter - handle Firestore Timestamp
    let matchesDate = true;
    if (filterDateRange !== 'all') {
      // Convert Firestore Timestamp to JavaScript Date
      const recordDate = record.createdAt?.toDate ? record.createdAt.toDate() : new Date(record.createdAt);
      const now = new Date();
      const days = filterDateRange === 'week' ? 7 : filterDateRange === 'month' ? 30 : 365;
      const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      matchesDate = recordDate >= cutoff;
    }
    
    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span>Loading activity records...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - same as before */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            Activity Records
          </h2>
          <p className="text-gray-600 mt-1">
            Track daily organizational activities and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('analytics')}
              className={`px-3 py-1 text-sm rounded ${viewMode === 'analytics' ? 'bg-white shadow-sm' : ''}`}
            >
              Analytics
            </button>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Log Activity
          </button>
        </div>
      </div>

      {viewMode === 'analytics' ? (
        <AnalyticsDashboard records={records} />
      ) : (
        <>
          {/* Filters with Status Buttons */}
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Status Filter Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-1 text-sm rounded ${
                    filterStatus === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {Object.entries(STATUS_TYPES).map(([key, status]) => {
                  const Icon = status.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setFilterStatus(key)}
                      className={`px-3 py-1 text-sm rounded flex items-center gap-1 ${
                        filterStatus === key ? status.btnColorActive : status.btnColor
                      }`}
                    >
                      <Icon className="h-3 w-3" />
                      {status.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {Object.entries(ACTIVITY_TYPES).map(([key, type]) => (
                <option key={key} value={key}>{type.label}</option>
              ))}
            </select>

            <select
              value={filterDateRange}
              onChange={(e) => setFilterDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Time</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>

          {/* Records List - same display logic as before */}
          <div className="space-y-4">
            {filteredRecords.map(record => {
              const ActivityIcon = ACTIVITY_TYPES[record.activityType]?.icon || Activity;
              const StatusIcon = STATUS_TYPES[record.status]?.icon || CheckCircle;
              const duration = record.metrics?.actualDuration ? `${Math.floor(record.metrics.actualDuration / 60)}h ${record.metrics.actualDuration % 60}m` : 'N/A';
              
              return (
                <div key={record.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="flex flex-col items-center gap-2">
                        <ActivityIcon className="h-5 w-5 text-gray-400" />
                        <div className={`w-3 h-3 rounded-full ${STATUS_TYPES[record.status]?.bg}`}>
                          <StatusIcon className={`h-3 w-3 ${STATUS_TYPES[record.status]?.color}`} />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{record.title}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${STATUS_TYPES[record.status]?.bg} ${STATUS_TYPES[record.status]?.color}`}>
                            {STATUS_TYPES[record.status]?.label}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 mb-3">{record.description}</p>
                        
                        {/* Dynamic metrics display */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {record.enabledMetrics?.duration && (
                            <div>
                              <span className="text-gray-500">Duration:</span>
                              <p className="font-medium">{duration}</p>
                            </div>
                          )}
                          {record.enabledMetrics?.quality && record.metrics?.qualityScore && (
                            <div>
                              <span className="text-gray-500">Quality Score:</span>
                              <p className="font-medium">{record.metrics.qualityScore}/10</p>
                            </div>
                          )}
                          {record.enabledMetrics?.progress && record.metrics?.completionPercentage !== undefined && (
                            <div>
                              <span className="text-gray-500">Progress:</span>
                              <p className="font-medium">{record.metrics.completionPercentage}%</p>
                            </div>
                          )}
                          {record.enabledMetrics?.quantity && record.metrics?.quantityValue && (
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <p className="font-medium">{record.metrics.quantityValue} {record.metrics.quantityUnit}</p>
                            </div>
                          )}
                          {record.enabledMetrics?.amount && record.metrics?.amountValue && (
                            <div>
                              <span className="text-gray-500">Amount:</span>
                              <p className="font-medium">{record.metrics.amountCurrency}{record.metrics.amountValue}</p>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">Date:</span>
                            <p className="font-medium">
                              {new Date(record.createdAt?.toDate ? record.createdAt.toDate() : record.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        {/* References and Tags display (same as before) */}
                        {Object.values(record.references || {}).some(refs => refs?.length > 0) && (
                          <div className="mt-3">
                            <span className="text-gray-500 text-sm">References:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(record.references).map(([type, refs]) => 
                                refs?.map(ref => (
                                  <span key={`${type}-${ref}`} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                    {type}: {ref}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        )}

                        {record.tags && record.tags.length > 0 && (
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-1">
                              {record.tags.map(tag => (
                                <span key={tag} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4">
                      <button
                        onClick={() => handleEdit(record)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Challenges and Achievements display (same as before) */}
                  {(record.challenges || record.achievements) && (
                    <div className="border-t pt-3 mt-3">
                      {record.challenges && (
                        <div className="mb-2">
                          <span className="text-sm font-medium text-red-700">Challenges:</span>
                          <p className="text-sm text-gray-600">{record.challenges}</p>
                        </div>
                      )}
                      {record.achievements && (
                        <div>
                          <span className="text-sm font-medium text-green-700">Achievements:</span>
                          <p className="text-sm text-gray-600">{record.achievements}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {filteredRecords.length === 0 && (
            <div className="text-center py-8">
              <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No activity records found</h3>
              <p className="text-gray-500">
                {records.length === 0 ? 'Start logging your organizational activities.' : 'Try adjusting your search or filters.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Updated Form Modal with Fixed Overflow */}
      {showForm && (
        <ActivityRecordForm
          formData={formData}
          setFormData={setFormData}
          referenceData={referenceData}
          isEditing={!!editingRecord}
          onSubmit={handleSubmit}
          onClose={handleCloseForm}
          saving={saving}
        />
      )}
    </div>
  );
};

// Updated ActivityRecordForm with Fixed Overflow and Merged Metrics
const ActivityRecordForm = ({ 
  formData, 
  setFormData, 
  referenceData, 
  isEditing, 
  onSubmit, 
  onClose, 
  saving 
}) => {
  const [activeTab, setActiveTab] = useState('basic');

  const handleReferenceChange = (type, selectedItems) => {
    setFormData(prev => ({
      ...prev,
      references: {
        ...prev.references,
        [type]: selectedItems
      }
    }));
  };

  const handleMetricChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [field]: value
      }
    }));
  };

  const handleMetricToggle = (metricType) => {
    setFormData(prev => ({
      ...prev,
      enabledMetrics: {
        ...prev.enabledMetrics,
        [metricType]: !prev.enabledMetrics[metricType]
      }
    }));
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Activity },
    { id: 'metrics', label: 'Metrics', icon: BarChart3 },
    { id: 'references', label: 'References', icon: Users },
    { id: 'details', label: 'Details', icon: FileText }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl h-full max-h-[95vh] flex flex-col">
        <form onSubmit={onSubmit} className="flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
            <h3 className="text-lg font-semibold">
              {isEditing ? 'Edit Activity Record' : 'Log New Activity'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b flex-shrink-0">
            <nav className="flex space-x-8 px-6">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Form Content - Fixed Overflow */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'basic' && (
              <BasicInfoTab 
                formData={formData} 
                setFormData={setFormData} 
              />
            )}
            
            {activeTab === 'metrics' && (
              <MergedMetricsTab 
                formData={formData} 
                onMetricChange={handleMetricChange}
                onMetricToggle={handleMetricToggle}
              />
            )}
            
            {activeTab === 'references' && (
              <ReferencesTab 
                formData={formData} 
                referenceData={referenceData}
                onReferenceChange={handleReferenceChange} 
              />
            )}
            
            {activeTab === 'details' && (
              <DetailsTab 
                formData={formData} 
                setFormData={setFormData} 
              />
            )}
          </div>

          {/* Footer - Fixed */}
          <div className="flex justify-between items-center p-6 border-t bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" />
              <span>Auto-save enabled</span>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {isEditing ? 'Update Record' : 'Log Activity'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Basic Info Tab with Button Interfaces
const BasicInfoTab = ({ formData, setFormData }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Activity Title *</label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Enter a descriptive title for this activity"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Provide details about what was accomplished, discussed, or observed..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Activity Type</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(ACTIVITY_TYPES).map(([key, type]) => {
            const Icon = type.icon;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, activityType: key }))}
                className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-all ${
                  formData.activityType === key
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Status *</label>
        <div className="flex gap-3">
          {Object.entries(STATUS_TYPES).map(([key, status]) => {
            const Icon = status.icon;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, status: key }))}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                  formData.status === key
                    ? status.btnColorActive
                    : status.btnColor
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{status.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {STATUS_TYPES[formData.status]?.description}
        </p>
      </div>
    </div>
  </div>
);

// Merged Metrics Tab with Toggleable Metrics
const MergedMetricsTab = ({ formData, onMetricChange, onMetricToggle }) => {
  const handleDurationCalculation = () => {
    if (formData.metrics.durationType === 'datetime' && 
        formData.metrics.startDateTime && 
        formData.metrics.endDateTime) {
      const start = new Date(formData.metrics.startDateTime);
      const end = new Date(formData.metrics.endDateTime);
      const duration = Math.round((end - start) / (1000 * 60));
      if (duration > 0) {
        onMetricChange('actualDuration', duration);
      }
    }
  };

  const setCurrentTime = (field) => {
    const now = new Date();
    const isoString = now.toISOString().slice(0, 16); // Format for datetime-local input
    onMetricChange(field, isoString);
    
    // Auto-calculate duration if both start and end are set
    setTimeout(handleDurationCalculation, 100);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-900 mb-3">Select Metrics to Track</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(METRIC_TYPES).map(([key, metric]) => {
            const Icon = metric.icon;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onMetricToggle(key)}
                className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-all ${
                  formData.enabledMetrics[key]
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                <div className="text-left">
                  <div className="font-medium">{metric.label}</div>
                  <div className="text-xs text-gray-500">{metric.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Enabled Metrics Forms */}
      <div className="space-y-8">
        {/* Time Metric */}
        {formData.enabledMetrics.time && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-blue-600" />
              <h5 className="font-medium text-gray-900">Specific Time</h5>
            </div>
            <div className="flex gap-2">
              <input
                type="datetime-local"
                value={formData.metrics.specificTime}
                onChange={(e) => onMetricChange('specificTime', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setCurrentTime('specificTime')}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Now
              </button>
            </div>
          </div>
        )}

        {/* Duration Metric */}
        {formData.enabledMetrics.duration && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Timer className="h-4 w-4 text-blue-600" />
              <h5 className="font-medium text-gray-900">Duration Tracking</h5>
            </div>
            
            {/* Duration Type Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration Type</label>
              <div className="flex gap-2">
                {[
                  { value: 'datetime', label: 'Date & Time' },
                  { value: 'date', label: 'Dates Only' },
                  { value: 'time', label: 'Times Only' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onMetricChange('durationType', option.value)}
                    className={`px-3 py-1 text-sm rounded ${
                      formData.metrics.durationType === option.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Planned Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Planned Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.metrics.plannedDuration}
                  onChange={(e) => onMetricChange('plannedDuration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Expected duration"
                />
              </div>

              {/* Actual Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Actual Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.metrics.actualDuration}
                  onChange={(e) => onMetricChange('actualDuration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Actual time spent"
                />
              </div>

              {/* Start Input - varies based on durationType */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start {formData.metrics.durationType === 'date' ? 'Date' : formData.metrics.durationType === 'time' ? 'Time' : 'Date & Time'}
                </label>
                <div className="flex gap-2">
                  <input
                    type={formData.metrics.durationType === 'date' ? 'date' : formData.metrics.durationType === 'time' ? 'time' : 'datetime-local'}
                    value={
                      formData.metrics.durationType === 'date' ? formData.metrics.startDateOnly :
                      formData.metrics.durationType === 'time' ? formData.metrics.startTimeOnly :
                      formData.metrics.startDateTime
                    }
                    onChange={(e) => {
                      const field = formData.metrics.durationType === 'date' ? 'startDateOnly' :
                                   formData.metrics.durationType === 'time' ? 'startTimeOnly' :
                                   'startDateTime';
                      onMetricChange(field, e.target.value);
                      setTimeout(handleDurationCalculation, 100);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {formData.metrics.durationType !== 'date' && (
                    <button
                      type="button"
                      onClick={() => {
                        const field = formData.metrics.durationType === 'time' ? 'startTimeOnly' : 'startDateTime';
                        const value = formData.metrics.durationType === 'time' 
                          ? new Date().toTimeString().slice(0, 5)
                          : new Date().toISOString().slice(0, 16);
                        onMetricChange(field, value);
                        setTimeout(handleDurationCalculation, 100);
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Now
                    </button>
                  )}
                </div>
              </div>

              {/* End Input - varies based on durationType */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End {formData.metrics.durationType === 'date' ? 'Date' : formData.metrics.durationType === 'time' ? 'Time' : 'Date & Time'}
                </label>
                <div className="flex gap-2">
                  <input
                    type={formData.metrics.durationType === 'date' ? 'date' : formData.metrics.durationType === 'time' ? 'time' : 'datetime-local'}
                    value={
                      formData.metrics.durationType === 'date' ? formData.metrics.endDateOnly :
                      formData.metrics.durationType === 'time' ? formData.metrics.endTimeOnly :
                      formData.metrics.endDateTime
                    }
                    onChange={(e) => {
                      const field = formData.metrics.durationType === 'date' ? 'endDateOnly' :
                                   formData.metrics.durationType === 'time' ? 'endTimeOnly' :
                                   'endDateTime';
                      onMetricChange(field, e.target.value);
                      setTimeout(handleDurationCalculation, 100);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {formData.metrics.durationType !== 'date' && (
                    <button
                      type="button"
                      onClick={() => {
                        const field = formData.metrics.durationType === 'time' ? 'endTimeOnly' : 'endDateTime';
                        const value = formData.metrics.durationType === 'time' 
                          ? new Date().toTimeString().slice(0, 5)
                          : new Date().toISOString().slice(0, 16);
                        onMetricChange(field, value);
                        setTimeout(handleDurationCalculation, 100);
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Now
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Duration Analysis */}
            {formData.metrics.plannedDuration && formData.metrics.actualDuration && (
              <div className="mt-4 p-3 bg-white rounded border">
                <h6 className="font-medium mb-2 text-sm">Duration Analysis</h6>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Planned:</span>
                    <p className="font-medium">{Math.floor(formData.metrics.plannedDuration / 60)}h {formData.metrics.plannedDuration % 60}m</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Actual:</span>
                    <p className="font-medium">{Math.floor(formData.metrics.actualDuration / 60)}h {formData.metrics.actualDuration % 60}m</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Variance:</span>
                    <p className={`font-medium ${
                      formData.metrics.actualDuration > formData.metrics.plannedDuration ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formData.metrics.actualDuration > formData.metrics.plannedDuration ? '+' : ''}
                      {Math.floor((formData.metrics.actualDuration - formData.metrics.plannedDuration) / 60)}h {Math.abs(formData.metrics.actualDuration - formData.metrics.plannedDuration) % 60}m
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quantity Metric */}
        {formData.enabledMetrics.quantity && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <h5 className="font-medium text-gray-900">Quantity/Count Measurement</h5>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Quantity Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onMetricChange('quantityType', 'single')}
                  className={`px-3 py-1 text-sm rounded ${
                    formData.metrics.quantityType === 'single'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Single Value
                </button>
                <button
                  type="button"
                  onClick={() => onMetricChange('quantityType', 'range')}
                  className={`px-3 py-1 text-sm rounded ${
                    formData.metrics.quantityType === 'range'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Start/End Range
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Use range for readings like odometer, inventory counts, etc.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.metrics.quantityType === 'single' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quantity Value</label>
                  <input
                    type="number"
                    value={formData.metrics.quantityValue}
                    onChange={(e) => onMetricChange('quantityValue', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 25, 150, 8"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Reading</label>
                    <input
                      type="number"
                      value={formData.metrics.quantityStart}
                      onChange={(e) => onMetricChange('quantityStart', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Starting value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Reading</label>
                    <input
                      type="number"
                      value={formData.metrics.quantityEnd}
                      onChange={(e) => onMetricChange('quantityEnd', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ending value"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                <input
                  type="text"
                  value={formData.metrics.quantityUnit}
                  onChange={(e) => onMetricChange('quantityUnit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., units, km, pages, people"
                />
              </div>
            </div>

            {/* Quantity Analysis for Range */}
            {formData.metrics.quantityType === 'range' && 
             formData.metrics.quantityStart && 
             formData.metrics.quantityEnd && (
              <div className="mt-4 p-3 bg-white rounded border">
                <h6 className="font-medium mb-2 text-sm">Quantity Analysis</h6>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Start:</span>
                    <p className="font-medium">{formData.metrics.quantityStart} {formData.metrics.quantityUnit}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">End:</span>
                    <p className="font-medium">{formData.metrics.quantityEnd} {formData.metrics.quantityUnit}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Difference:</span>
                    <p className="font-medium text-blue-600">
                      {(formData.metrics.quantityEnd - formData.metrics.quantityStart)} {formData.metrics.quantityUnit}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quality Metric */}
        {formData.enabledMetrics.quality && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-blue-600" />
              <h5 className="font-medium text-gray-900">Quality Assessment</h5>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Quality Score (1-10)</label>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {[...Array(10)].map((_, i) => {
                  const score = i + 1;
                  return (
                    <button
                      key={score}
                      type="button"
                      onClick={() => onMetricChange('qualityScore', score.toString())}
                      className={`p-2 text-sm font-medium rounded border transition-all ${
                        parseInt(formData.metrics.qualityScore) === score
                          ? score <= 3 ? 'bg-red-600 text-white border-red-600' :
                            score <= 6 ? 'bg-yellow-600 text-white border-yellow-600' :
                            score <= 8 ? 'bg-blue-600 text-white border-blue-600' :
                            'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {score}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {formData.metrics.qualityScore && (
                  <span>
                    Rating: {
                      parseInt(formData.metrics.qualityScore) <= 3 ? 'Poor' :
                      parseInt(formData.metrics.qualityScore) <= 6 ? 'Average' :
                      parseInt(formData.metrics.qualityScore) <= 8 ? 'Good' : 'Excellent'
                    }
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Progress Metric */}
        {formData.enabledMetrics.progress && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <h5 className="font-medium text-gray-900">Progress Tracking</h5>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Completion Percentage</label>
              <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={formData.metrics.completionPercentage}
                  onChange={(e) => onMetricChange('completionPercentage', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>0%</span>
                  <span className="font-medium text-lg text-blue-600">{formData.metrics.completionPercentage}%</span>
                  <span>100%</span>
                </div>
                
                {/* Quick percentage buttons */}
                <div className="flex gap-2">
                  {[0, 25, 50, 75, 100].map(percent => (
                    <button
                      key={percent}
                      type="button"
                      onClick={() => onMetricChange('completionPercentage', percent)}
                      className={`px-3 py-1 text-sm rounded ${
                        formData.metrics.completionPercentage === percent
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Amount/Financial Metric */}
        {formData.enabledMetrics.amount && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-4 w-4 text-blue-600" />
              <h5 className="font-medium text-gray-900">Financial Impact</h5>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <div className="flex gap-1">
                  {[
                    { value: 'cost', label: 'Cost', color: 'bg-red-100 text-red-700' },
                    { value: 'income', label: 'Income', color: 'bg-green-100 text-green-700' },
                    { value: 'saving', label: 'Saving', color: 'bg-blue-100 text-blue-700' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => onMetricChange('amountType', type.value)}
                      className={`px-3 py-1 text-sm rounded flex-1 ${
                        formData.metrics.amountType === type.value
                          ? type.color
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={formData.metrics.amountCurrency}
                  onChange={(e) => onMetricChange('amountCurrency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="R">R (ZAR)</option>
                  <option value="$">$ (USD)</option>
                  <option value="€">€ (EUR)</option>
                  <option value="£">£ (GBP)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.metrics.amountValue}
                  onChange={(e) => onMetricChange('amountValue', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            {formData.metrics.amountValue && (
              <div className="mt-3 p-3 bg-white rounded border">
                <span className="text-sm text-gray-500">Financial Impact: </span>
                <span className={`font-medium ${
                  formData.metrics.amountType === 'cost' ? 'text-red-600' :
                  formData.metrics.amountType === 'income' ? 'text-green-600' :
                  'text-blue-600'
                }`}>
                  {formData.metrics.amountType === 'cost' ? '-' : '+'}
                  {formData.metrics.amountCurrency}{parseFloat(formData.metrics.amountValue).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// References Tab (keep mostly the same)
const ReferencesTab = ({ formData, referenceData, onReferenceChange }) => {
  const referenceTypes = [
    { key: 'teamMembers', label: 'Team Members', data: referenceData.team, displayField: 'name' },
    { key: 'sites', label: 'Sites/Locations', data: referenceData.sites, displayField: 'name' },
    { key: 'assets', label: 'Assets/Equipment', data: referenceData.assets, displayField: 'name' },
    { key: 'tasks', label: 'Related Tasks', data: referenceData.tasks, displayField: 'title' },
    { key: 'clients', label: 'Clients/Customers', data: referenceData.clients, displayField: 'name' },
    { key: 'documents', label: 'Documents', data: referenceData.documents, displayField: 'name' }
  ];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Link this activity to related organizational entities for better tracking and analysis.
      </p>
      
      {referenceTypes.map(type => (
        <div key={type.key}>
          <label className="block text-sm font-medium text-gray-700 mb-2">{type.label}</label>
          <ReferenceSelector
            options={type.data}
            selected={formData.references[type.key] || []}
            onChange={(selected) => onReferenceChange(type.key, selected)}
            displayField={type.displayField}
            placeholder={`Select ${type.label.toLowerCase()}...`}
          />
        </div>
      ))}
    </div>
  );
};

// Reference Selector Component (keep the same)
const ReferenceSelector = ({ options, selected, onChange, displayField, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleToggle = (option) => {
    const isSelected = selected.includes(option.id);
    if (isSelected) {
      onChange(selected.filter(id => id !== option.id));
    } else {
      onChange([...selected, option.id]);
    }
  };

  const selectedItems = options.filter(opt => selected.includes(opt.id));

  return (
    <div className="relative">
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer min-h-[42px] flex items-center justify-between"
      >
        <div className="flex flex-wrap gap-1">
          {selectedItems.length > 0 ? (
            selectedItems.map(item => (
              <span key={item.id} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-sm rounded">
                {item[displayField]}
              </span>
            ))
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.length > 0 ? options.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleToggle(option)}
              className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                selected.includes(option.id) ? 'bg-blue-50 text-blue-700' : ''
              }`}
            >
              <span>{option[displayField]}</span>
              {selected.includes(option.id) && <CheckCircle className="h-4 w-4" />}
            </button>
          )) : (
            <div className="px-3 py-2 text-gray-500 text-sm">No items available</div>
          )}
        </div>
      )}
    </div>
  );
};

// Details Tab (keep mostly the same)
const DetailsTab = ({ formData, setFormData }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Challenges Encountered</label>
      <textarea
        value={formData.challenges}
        onChange={(e) => setFormData(prev => ({ ...prev, challenges: e.target.value }))}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
        placeholder="Describe any obstacles, delays, or problems encountered..."
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Achievements & Successes</label>
      <textarea
        value={formData.achievements}
        onChange={(e) => setFormData(prev => ({ ...prev, achievements: e.target.value }))}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
        placeholder="Highlight what went well, milestones reached, or positive outcomes..."
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Next Actions Required</label>
      <textarea
        value={formData.nextActions}
        onChange={(e) => setFormData(prev => ({ ...prev, nextActions: e.target.value }))}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
        placeholder="What needs to be done next? Follow-up actions, decisions pending..."
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
      <input
        type="text"
        value={formData.tags}
        onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="Add tags for easier searching (comma-separated): urgent, weekly-review, client-facing..."
      />
      <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
    </div>

    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id="isPublic"
        checked={formData.isPublic}
        onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
      />
      <label htmlFor="isPublic" className="text-sm text-gray-700">
        Make this record visible to all team members
      </label>
    </div>
  </div>
);

// Analytics Dashboard Component (keep the same as before)
const AnalyticsDashboard = ({ records }) => {
  const analytics = React.useMemo(() => {
    const total = records.length;
    const statusCounts = {
      green: records.filter(r => r.status === 'green').length,
      orange: records.filter(r => r.status === 'orange').length,
      red: records.filter(r => r.status === 'red').length
    };

    const totalHours = records.reduce((sum, r) => sum + (r.metrics?.actualDuration || 0), 0) / 60;
    const avgQuality = records
      .filter(r => r.metrics?.qualityScore)
      .reduce((sum, r, _, arr) => sum + r.metrics.qualityScore / arr.length, 0);

    return { total, statusCounts, totalHours, avgQuality };
  }, [records]);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center gap-3">
            <Activity className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Total Activities</p>
              <p className="text-2xl font-bold">{analytics.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">On Track</p>
              <p className="text-2xl font-bold text-green-600">{analytics.statusCounts.green}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600">Need Attention</p>
              <p className="text-2xl font-bold text-orange-600">{analytics.statusCounts.orange}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-600">Critical Issues</p>
              <p className="text-2xl font-bold text-red-600">{analytics.statusCounts.red}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="font-semibold mb-4">Performance Overview</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span>Total Hours Logged:</span>
              <span className="font-medium">{Math.round(analytics.totalHours)}h</span>
            </div>
            <div className="flex justify-between">
              <span>Average Quality Score:</span>
              <span className="font-medium">{analytics.avgQuality.toFixed(1)}/10</span>
            </div>
            <div className="flex justify-between">
              <span>Success Rate:</span>
              <span className="font-medium text-green-600">
                {Math.round((analytics.statusCounts.green / analytics.total) * 100)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <h3 className="font-semibold mb-4">Status Distribution</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>On Track</span>
              </div>
              <span>{analytics.statusCounts.green}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span>Needs Attention</span>
              </div>
              <span>{analytics.statusCounts.orange}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>Critical Issues</span>
              </div>
              <span>{analytics.statusCounts.red}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};